import {
  IconAlarm,
  IconBell,
  IconChartAreaLine,
  IconDashboard,
  IconReport,
  IconUserDollar,
} from '@tabler/icons-react';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys, useTranslation } from 'locales';
import { useMemo } from 'react';
import { useLoadNotificationsCount } from 'shared/hooks/use-load-notifications-count';
import type { Menu, MenuItem } from '../types';
import { isMenu } from '../utils';
import { useBuildBankAccountMenu } from './use-build-bank-account-menu';
import { useBuildFollowupMenu } from './use-build-followup-menu';
import { useBuildInvoiceMenu } from './use-build-invoice-menu';
import { useBuildNeedsHandlingMenu } from './use-build-needs-handling-menu';
import { useBuildRemindersMenu } from './use-build-reminders-menu';
import { useBuildSettingsMenu } from './use-build-settings-menu';

export const populateParent = (menu: Menu, parent: Menu | null): Menu => {
  menu.parent = parent;
  menu.items.map(item => {
    if (!isMenu(item)) return null;
    return populateParent(item, menu);
  });

  return menu;
};

export function useBuildMenuItems(): Menu {
  const { t } = useTranslation();
  const company = useCompanyConfiguration();
  const { notificationsCount } = useLoadNotificationsCount();

  const invoiceMenu = useBuildInvoiceMenu();
  const followUpMenu = useBuildFollowupMenu();
  const remindersMenu = useBuildRemindersMenu();
  const bankAccountMenu = useBuildBankAccountMenu();
  const settingsMenu = useBuildSettingsMenu();
  const needsHandlingMenu = useBuildNeedsHandlingMenu();

  return useMemo(() => {
    const items: Array<MenuItem | Menu> = [
      {
        path: '/dashboard',
        label: t(i18nKeys.COMMON.DASHBOARD),
        Icon: IconDashboard,
      },
      needsHandlingMenu,
      followUpMenu,
      remindersMenu,
      {
        path: '/clients',
        label: t(i18nKeys.CLIENTS),
        Icon: IconUserDollar,
        isExactRouteMatch: false,
      },
      invoiceMenu,
      bankAccountMenu,
      {
        path: '/analytics',
        label: 'Analytics',
        Icon: IconChartAreaLine,
        showUpsell: !company.package?.can_use_analytics,
        isExactRouteMatch: false,
      },
      {
        path: '/reports',
        label: t(i18nKeys.REPORTS.INDEX.TITLE),
        Icon: IconReport,
        showUpsell: !company.package?.can_use_reports,
      },
      {
        path: '/activities',
        label: t(i18nKeys.NAV.ACTIVITY_BOOK),
        Icon: IconBell,
        unreadCount: notificationsCount,
      },
      settingsMenu,
    ];

    const menu: Menu = {
      parent: null,
      label: 'root',
      Icon: IconAlarm,
      items,
    };

    return populateParent(menu, null);
  }, [company, followUpMenu, bankAccountMenu, invoiceMenu, notificationsCount, settingsMenu, t]);
}
